import React, { useContext, useState, useEffect } from 'react';
import { CartContext } from '../context/CartContext';
import { SettingsContext } from '../context/SettingsContext';
import {
  Typography,
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { insererCommande } from '../utils/api';
import { generateMergedCartData } from '../utils/mergeCartData';
import './CartSummaryPage.css';

const CartSummaryPage = () => {
  const { cartItems, total, clearCart, menuChoices } = useContext(CartContext);
  const { vacances } = useContext(SettingsContext);
  const [clientInfo, setClientInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    commentaire: '',
    pickup_datetime: '',
    payment_method: 'Espèces'
  });
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('info');
  const [mergedCartData, setMergedCartData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false); // État pour l'overlay
  const [isOrderSent, setIsOrderSent] = useState(false); // État pour la commande envoyée

  // Initialiser pickup_datetime à now + 30 min soit 150 min a cause du décalage de 2h du serveur
  useEffect(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 150);
    const localISOTime = now.toISOString().slice(0, 16); // format "YYYY-MM-DDTHH:mm"
    setClientInfo((prev) => ({ ...prev, pickup_datetime: localISOTime }));
  }, []);

  // Générer le JSON unique lors du chargement de la page
  useEffect(() => {
    const cartState = { cartItems, menuChoices };
    const mergedData = generateMergedCartData(cartState);
    setMergedCartData(mergedData);
    // console.log('JSON unique généré :', JSON.stringify(mergedData, null, 2));
  }, [cartItems, menuChoices]);

  // Dynamically set title and meta description for summary page
  useEffect(() => {
    fetch(`/assets/json/infos-generale.json?v=${Date.now()}`)
      .then(res => res.json())
      .then(data => {
        if (data.titleSummary) {
          document.title = data.titleSummary;
        }
        if (data.metaDescriptionSummary) {
          let meta = document.querySelector('meta[name="description"]');
          if (!meta) {
            meta = document.createElement('meta');
            meta.name = "description";
            document.head.appendChild(meta);
          }
          meta.content = data.metaDescriptionSummary;
        }
      });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientInfo({ ...clientInfo, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (vacances.active) return;
    setIsSubmitting(true); // Activer l'overlay

    const orderDetails = {
      ...clientInfo,
      cartItems: mergedCartData, // Utiliser le JSON fusionné ici
      total
    };

    try {
      const response = await insererCommande(orderDetails);
     // console.log('✅ Commande enregistrée avec succès :', response);
      setMessage("✅ Votre commande a bien été enregistrée ! Attendez la confirmation par email.");
      setMessageType("success");
      setIsOrderSent(true); // Marquer la commande comme envoyée

      // ⏳ Décaler légèrement le clearCart pour que le message s'affiche d'abord
      setTimeout(() => {
        clearCart();
        setIsSubmitting(false); // Désactiver l'overlay après le message
      }, 5000); // 5 secondes d’attente

    } catch (error) {
      console.error('❌ Erreur lors de l’enregistrement :', error);
      setMessage("❌ Une erreur s'est produite lors de l'enregistrement de votre commande.");
      setMessageType("error");
      setIsSubmitting(false); // Désactiver l'overlay en cas d'erreur
    }
  };

  const isEmpty = mergedCartData.length === 0;

  return (
    <div className="cart-summary-page responsive-cart-summary">
      {isSubmitting && (
        <div className="overlay">
          <div className="overlay-content">
            <CircularProgress size={50} color="inherit" />
            <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
              La commande est en cours d'envoi.<br />
              Une fois envoyée, veuillez attendre la confirmation du restaurant par email ou SMS.
            </Typography>
          </div>
        </div>
      )}
      <Typography className='recapTitle' variant="h4" gutterBottom>Récapitulatif de votre commande</Typography>

      {isOrderSent ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          ✅ Votre commande a été envoyée avec succès ! Veuillez attendre la confirmation du restaurant par email ou SMS.
        </Alert>
      ) : vacances.active ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {vacances.message
            ? vacances.message
            : `Nous sommes actuellement fermés pour congés du ${vacances.debut} au ${vacances.fin}. Les commandes en ligne sont temporairement indisponibles.`}
        </Alert>
      ) : isEmpty ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          🛒 Votre panier est vide. Veuillez ajouter des produits avant de passer commande.
        </Alert>
      ) : (
        <>
          <List>
            {mergedCartData.map((item, index) => (
              <ListItem key={index} alignItems="flex-start" style={{ flexDirection: 'column', alignItems: 'stretch', background: '#f8f8f8', borderRadius: 8, marginBottom: 10, padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.nom} <span style={{ color: '#888', fontWeight: 400 }}>x {item.quantity || 1}</span>
                  </Typography>
                  {item.prix && (
                    <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 500 }}>
                      {item.prix} €
                    </Typography>
                  )}
                </div>
                {item.menuDetails ? (
                  <Box sx={{ mt: 1, ml: 1.5, mb: 0.5, fontSize: '0.97em', color: '#444' }}>
                    {item.menuDetails.selectedItems ? (
                      <>
                        
                        <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                          {item.menuDetails.selectedItems.map((i, idx) => (
                            <li key={idx} style={{ marginBottom: 2 }}>{i.nom}</li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <div>
                        {item.menuDetails.selectedFormula && (
                          <div className='blocFormule'><span>Formule choisie :</span> {item.menuDetails.selectedFormula}</div>
                        )}
                        {item.menuDetails.selectedEntrée && (
                          <div><span style={{ fontWeight: 500 }}>Entrée :</span> {item.menuDetails.selectedEntrée.nom}</div>
                        )}
                        {item.menuDetails.selectedPlat && (
                          <div><span style={{ fontWeight: 500 }}>Plat :</span> {item.menuDetails.selectedPlat.nom}</div>
                        )}
                        {item.menuDetails.selectedGarniture && (
                          <div><span style={{ fontWeight: 500 }}>Garniture :</span> {item.menuDetails.selectedGarniture.nom}</div>
                        )}
                        {item.menuDetails.selectedDessert && (
                          <div><span style={{ fontWeight: 500 }}>Dessert :</span> {item.menuDetails.selectedDessert.nom}</div>
                        )}
                      </div>
                    )}
                  </Box>
                ) : null}
              </ListItem>
            ))}
            <ListItem style={{ marginBottom:"35px"}}>
              <ListItemText primary="Total" />
              <Typography variant="subtitle1">{total} €</Typography>
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2, mb: 3, fontSize: '1.2em' }}>
          ⚠️ La commande est à récupérer <strong>au restaurant</strong>. Aucun service de livraison n’est proposé.
          <br />Toute commande passée est due.
          </Alert>

          <Box component="form" onSubmit={handleSubmit} className="client-form">
          <Typography variant="h6" gutterBottom>Vos informations</Typography>

          <TextField
            label="Prénom"
            name="firstName"
            value={clientInfo.firstName}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Nom"
            name="lastName"
            value={clientInfo.lastName}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Email"
            name="email"
            value={clientInfo.email}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Téléphone"
            name="phone"
            value={clientInfo.phone}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required
          />

          <TextField
            label="Date et heure de retrait souhaitée"
            type="datetime-local"
            name="pickup_datetime"
            value={clientInfo.pickup_datetime || ''}
            onChange={(e) => {
            const selectedDate = new Date(e.target.value);
            const now = new Date();
            now.setMinutes(now.getMinutes() + 60); // 1h30 à partir de maintenant

            if (selectedDate < now) {
              setMessage("❌ La date et l'heure de retrait doivent être au moins 1h dans le futur.");
              setMessageType("error");
            } else {
              setMessage(null);
              handleInputChange(e);
            }
            }}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
            required
            error={messageType === "error" && message !== null}
            helperText={messageType === "error" && message ? message : ''}
            onClick={(e) => e.target.showPicker()} // Ouvre le datepicker lors du clic dans le champ
          />

          <TextField
            label="Commentaire (optionnel)"
            name="commentaire"
            value={clientInfo.commentaire}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />

          <FormControl fullWidth margin="normal">
            <FormLabel>Mode de paiement</FormLabel>
            <RadioGroup
            row
            name="payment_method"
            value={clientInfo.payment_method}
            onChange={handleInputChange}
            >
            <FormControlLabel value="Espèces" control={<Radio />} label="Espèces" />
            <FormControlLabel value="CB" control={<Radio />} label="Carte Bancaire" />
            </RadioGroup>
          </FormControl>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            disabled={isSubmitting} // Désactiver le bouton pendant le traitement
        >
          Valider la commande
        </Button>
      </Box>
    </>
  )}
  </div>
);
};

export default CartSummaryPage;
